/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useCallback } from "react";
import "./styles.css";
// import "./EnhancedFaceGuide.css"; // Delete this line if you merged the CSS
import CameraControls from "./components/CameraControls";
import EnhancedFaceGuide from "./components/EnhancedFaceGuide";
// import StatusIndicator from "./components/StatusIndicator";
import useCamera from "./hooks/useCamera";
import useFaceValidation from "./hooks/useFaceValidation";

// Main camera component - simple and focused
const CameraComponent = ({ onCapture, onClose, distanceConfig = {} }) => {
  const modalRef = useRef(null);

  // Camera hook - handles all camera logic
  const {
    videoRef,
    isReady: cameraReady,
    error: cameraError,
    isFrontCamera,
    availableCameras,
    currentResolution,
    switchCamera,
    capturePhoto,
    startCamera,
  } = useCamera("balanced");

  // Face validation hook - handles face detection
  const {
    isValid: facePositionValid,
    // message: faceMessage,
    isModelLoaded,
    currentFaceData,
    canvasRef,
  } = useFaceValidation(videoRef, distanceConfig);

  // Handle outside click to close modal
  const handleOutsideClick = useCallback(
    (e) => {
      if (modalRef.current && e.target === modalRef.current) {
        onClose();
      }
    },
    [onClose]
  );

  // Handle camera refresh
  const handleRefreshCamera = useCallback(() => {
    startCamera();
  }, [startCamera]);

  // Switch camera handler
  const handleSwitchCamera = useCallback(
    (e) => {
      e.preventDefault();
      e.target.blur();
      switchCamera();
    },
    [switchCamera]
  );

  // Handle photo capture
  const handleCapture = useCallback(
    (e) => {
      if (e) e.preventDefault();

      if (!cameraReady || !isModelLoaded || !facePositionValid) return;

      const imageDataUrl = capturePhoto();
      if (imageDataUrl) {
        onCapture(imageDataUrl);
      }
    },
    [cameraReady, isModelLoaded, facePositionValid, capturePhoto, onCapture]
  );

  // Handle permission denied case
  if (
    cameraError === "נדרשת הרשאה לשימוש במצלמה" ||
    cameraError === "Camera permission denied"
  ) {
    return (
      <div className="camera-permission-denied">
        <div className="permission-message">
          <h3>נדרשת גישה למצלמה</h3>
          <button onClick={onClose} className="close-button">
            סגירה
          </button>
        </div>
      </div>
    );
  }

  // Handle general errors
  if (cameraError) {
    return (
      <div className="camera-permission-denied">
        <div className="permission-message">
          <h3>שגיאה במצלמה</h3>
          <p>{cameraError}</p>
          <div
            style={{ display: "flex", gap: "10px", justifyContent: "center" }}
          >
            <button onClick={handleRefreshCamera} className="close-button">
              נסה שוב
            </button>
            <button onClick={onClose} className="close-button">
              סגירה
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state check
  const isLoading = !cameraReady || !isModelLoaded;

  return (
    <div className="camera-modal" ref={modalRef} onClick={handleOutsideClick}>
      <div className="camera-modal-content">
        <div className="camera-header">
          <h2 className="camera-title">צילום תמונה</h2>
          <button onClick={onClose} className="close-camera-button">
            ✕
          </button>
        </div>

        <div className="camera-container">
          {/* Always render video element - hidden during loading */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`camera-video ${
              isFrontCamera ? "front-camera" : "back-camera"
            }`}
            style={{ visibility: isLoading ? "hidden" : "visible" }}
          />

          <canvas
            ref={canvasRef}
            className={`detection-canvas ${
              isFrontCamera ? "front-camera" : "back-camera"
            }`}
            style={{
              display: "none", // Completely hide - no visual interference
            }}
          />

          {/* Loading overlay */}
          {isLoading && (
            <div className="camera-loading-overlay">
              <div className="loading-spinner" />
              <p>
                {!cameraReady
                  ? "מפעיל מצלמה..."
                  : !isModelLoaded
                  ? "טוען מודל זיהוי פנים..."
                  : "מכין הכל..."}
              </p>
            </div>
          )}

          {/* UI elements - only when ready */}
          {!isLoading && (
            <>
              <EnhancedFaceGuide
                isValid={facePositionValid}
                currentFaceData={currentFaceData}
                videoSize={{
                  width: videoRef.current?.videoWidth || 100,
                  height: videoRef.current?.videoHeight || 100,
                }}
                isFrontCamera={isFrontCamera} // Control ellipse size - percentages of screen size
                ellipseWidth={37} // 35% = medium ellipse
                ellipseHeight={46} // 55% = medium ellipse
              />

              <CameraControls
                availableCameras={availableCameras}
                cameraReady={cameraReady}
                isModelLoaded={isModelLoaded}
                facePositionValid={facePositionValid}
                onSwitchCamera={handleSwitchCamera}
                onCapture={handleCapture}
                onRefreshCamera={handleRefreshCamera}
              />
            </>
          )}
        </div>

        <div className="camera-instructions">
          <h4>הנחיות לצילום מיטבי:</h4>
          <ul>
            <li>ודא שפניך במרכז</li>
            <li>התקרב/התרחק בהתאם להודעה</li>
            <li>ודא תאורה טובה לאיכות מיטבית</li>
          </ul>
          {!isLoading &&
            videoRef.current &&
            videoRef.current.videoWidth > 0 &&
            currentResolution && (
              <div className="resolution-info">
                <p className="resolution-text">
                  {currentResolution.width}x{currentResolution.height}
                </p>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default CameraComponent;
